import { useCallback, useEffect, useState } from 'react';

import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ResponsiveContainer } from 'recharts';
import squarify from 'squarify';

import { getInterpolatedColor, linearInterpolation } from 'utils/colors';
import commonPropTypes from 'utils/commonPropTypes';
import { renderSvgTextSpans, svgShadowDef } from 'utils/svg';

import * as svars from 'assets/style/variables';

import SentimentScale from '../SentimentScale';
import Tooltip from './TreemapTooltip';

const sortData = (data) => {
  data.forEach((element) => {
    element.value = element.n_chunks;
  });
  data.sort((a, b) => (a.value < b.value ? 1 : -1));
};

const StyledTreemapBlock = styled.g`
  cursor: pointer;

  & rect {
    overflow: visible;
    stroke-width: ${svars.treemapHoverFrameWidth};
    stroke: ${svars.colorWhite};
    shape-rendering: crispEdges;
  }

  & text {
    fill: ${svars.fontColorBase};
    font-weight: ${svars.fontWeightBold};
  }
`;

export const treemapItemPropTypes = PropTypes.shape({
  id: PropTypes.string,
  parent_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  n_chunks: PropTypes.number,
  average_sentiment: PropTypes.number,
  share_of_extracts: PropTypes.number,
});

function Treemap({
  data,
  width,
  height,
  onClick,
  nodeId,
  tooltipVolumeLabel,
  tooltipSentimentLabel,
  tooltipShareOfExtractsLabel,
  showScale,
  minSentiment,
  maxSentiment,
  tooltipElements,
}) {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [squarifiedData, setSquarifiedData] = useState([]);
  const [maxOffsetX, setMaxOffsetX] = useState(0);
  const [maxOffsetY, setMaxOffsetY] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({});

  const resetData = useCallback(() => {
    sortData(data);
    const container = {
      x0: 0,
      y0: 0,
      x1: width,
      y1: height - svars.treemapColorScaleHeight,
    };
    const squarified = squarify(data, container);
    setSquarifiedData(squarified);
    setMaxOffsetX(Math.max(...squarified.map(({ x1 }) => x1)));
    setMaxOffsetY(Math.max(...squarified.map(({ y1 }) => y1)));
  }, [data, width, height]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  useEffect(() => {
    const handleMouseMove = throttle((pos) => {
      setCursorPosition({ offsetX: pos.offsetX, offsetY: pos.offsetY });
    }, 50);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    resetData();
  }, [width, height, data, resetData]);

  const onMouseLeave = useCallback(
    (node) => () => {
      if (hoveredNode && hoveredNode === node) {
        setHoveredNode(null);
      }
    },
    [hoveredNode]
  );

  const renderTreemapBlock = (node) => {
    const { id, x0, x1, y0, y1, label, average_sentiment } = node;
    const interpolatedColor = getInterpolatedColor(
      average_sentiment,
      linearInterpolation,
      svars.absoluteMinColor,
      svars.absoluteMaxColor,
      svars.absoluteMidColor
    );
    const isHovered = (hoveredNode && hoveredNode.id === id) || nodeId === id;
    const coloredBlockWidth = Math.max(
      1,
      x1 - x0 - 2 * svars.treemapHoverFrameWidth
    );
    const coloredBlockHeight = Math.max(
      1,
      y1 - y0 - 2 * svars.treemapHoverFrameWidth
    );
    return (
      <StyledTreemapBlock key={`treemap-block-${x0}-${id}`}>
        <rect
          onClick={() => onClick(id)}
          x={x0}
          y={svars.treemapColorScaleHeight + y0}
          width={x1 - x0}
          height={y1 - y0}
          fill={interpolatedColor}
          rx="8"
          ry="8"
          onMouseEnter={() => setHoveredNode(node)}
          onMouseLeave={onMouseLeave(node)}
          data-testid="treemap-block"
        />
        <rect
          x={x0 + svars.treemapHoverFrameWidth}
          y={svars.treemapColorScaleHeight + y0 + svars.treemapHoverFrameWidth}
          width={coloredBlockWidth}
          height={coloredBlockHeight}
          filter={isHovered ? 'url(#inset)' : undefined}
          fill="transparent"
          rx="3"
          ry="3"
          style={{ pointerEvents: 'none' }}
        />
        <text
          x={(x1 - x0) / 2 + x0}
          y={svars.treemapColorScaleHeight + (y1 - y0) / 2 + y0}
          dominantBaseline="middle"
          alignmentBaseline="hanging"
          textAnchor="middle"
          style={{ pointerEvents: 'none' }}
        >
          {renderSvgTextSpans(
            label,
            x0 + 2 * svars.treemapHoverFrameWidth,
            x0 + coloredBlockWidth + 1,
            svars.treemapColorScaleHeight +
              y0 +
              2 * svars.treemapHoverFrameWidth,
            svars.treemapColorScaleHeight +
              y1 -
              2 * svars.treemapHoverFrameWidth,
            interpolatedColor,
            true
          )}
        </text>
      </StyledTreemapBlock>
    );
  };

  return (
    <svg
      x={0}
      y={0}
      width={width}
      height={height}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <filter id="inset" x="-50%" y="-50%" width="400%" height="400%">
          <feFlood
            floodColor={svars.treemapHoverFrameColor}
            result="inside-color"
          />
          <feComposite in2="SourceAlpha" operator="in" result="inside-stroke" />
        </filter>
        <filter id="filter1" x="0" y="0">
          <feOffset result="offOut" in="SourceAlpha" dx="-20" dy="-20" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
      </defs>
      {svgShadowDef}
      {showScale && (
        <SentimentScale
          id="treemap"
          minSentiment={minSentiment}
          maxSentiment={maxSentiment}
          cursorSentiment={hoveredNode ? hoveredNode.average_sentiment : null}
        />
      )}
      <svg y={svars.treemapHoverFrameWidth}>
        {squarifiedData.map(renderTreemapBlock)}
      </svg>
      {hoveredNode != null ? (
        <Tooltip
          node={hoveredNode}
          elements={tooltipElements}
          cursorPosition={cursorPosition}
          maxOffsetX={maxOffsetX}
          maxOffsetY={maxOffsetY}
          tooltipVolumeLabel={tooltipVolumeLabel}
          tooltipSentimentLabel={tooltipSentimentLabel}
          tooltipShareOfExtractsLabel={tooltipShareOfExtractsLabel}
        />
      ) : null}
    </svg>
  );
}

Treemap.propTypes = {
  data: PropTypes.arrayOf(treemapItemPropTypes).isRequired,
  tooltipSentimentLabel: commonPropTypes.i18nText.isRequired,
  tooltipVolumeLabel: commonPropTypes.i18nText.isRequired,
  tooltipShareOfExtractsLabel: commonPropTypes.i18nText.isRequired,
  nodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
  showScale: PropTypes.bool,
  minSentiment: PropTypes.number,
  maxSentiment: PropTypes.number,
  tooltipElements: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: commonPropTypes.i18nText,
      Cell: PropTypes.elementType,
    })
  ).isRequired,
};

Treemap.defaultProps = {
  nodeId: null,
  showScale: true,
  onClick: () => {},
  minSentiment: -1.0,
  maxSentiment: 1.0,
  height: undefined,
  width: undefined,
};

function ResponsiveTreemap({ width, height, ...otherProps }) {
  return (
    <ResponsiveContainer width={width} height={height}>
      <Treemap {...otherProps} />
    </ResponsiveContainer>
  );
}

ResponsiveTreemap.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
ResponsiveTreemap.defaultProps = {
  height: 400,
  width: '100%',
};

export default ResponsiveTreemap;
